import React, { ReactElement } from "react";
import clsx from "clsx";
import {
  BlockContentValue,
  ButtonsContainer,
  ImageAspectRatio,
  isSanityImagePresent,
  ResponsiveSanityImage,
  ResponsiveSanityImageProps,
  SanityImageRatio,
  TextStyle
} from "@silversea-ssc/ui-components-react";
import * as styles from "./FeaturedContentRow.module.scss";
import { BlockContent } from "components/BlockContent";

export type FeautedContentRowImageSizeType = "regular" | "small";

interface FeaturedContentRowProps {
  className?: string;
  image?: ResponsiveSanityImageProps["image"];
  imageSize?: FeautedContentRowImageSizeType;
  tagline?: string | JSX.Element;
  title: string;
  description: BlockContentValue;
  buttons?: JSX.Element[];
  imageUrlLink?: string;
  imageRatio?: ImageAspectRatio;
  dataContentName?: string;
}

interface WrapImageWithLinkProps {
  imageUrlLink?: string;
  image: ReactElement;
}

const WrapImageWithLink: React.FC<
  React.PropsWithChildren<WrapImageWithLinkProps>
> = ({ imageUrlLink, image }) => {
  return imageUrlLink ? <a href={imageUrlLink}>{image}</a> : image;
};

const FeaturedContentRow: React.FC<
  React.PropsWithChildren<FeaturedContentRowProps>
> = ({
  className,
  image,
  imageSize = "regular",
  buttons,
  tagline,
  title,
  description,
  imageUrlLink,
  imageRatio = "auto" as const,
  dataContentName
}) => {
  const hasImage = isSanityImagePresent(image);

  return (
    <div
      className={clsx(styles.row, className, {
        [styles.withRegularImage]: hasImage && imageSize == "regular",
        [styles.withSmallImage]: hasImage && imageSize == "small"
      })}
      data-content-name={dataContentName}
    >
      {hasImage && (
        <div className={styles.images}>
          <WrapImageWithLink
            image={
              <SanityImageRatio image={image} ratio={imageRatio}>
                <ResponsiveSanityImage
                  image={image}
                  defaultWidth={360}
                  defaultHeight={300}
                  fit={imageRatio == "auto" ? "original" : "crop"}
                />
              </SanityImageRatio>
            }
            imageUrlLink={imageUrlLink}
          />
        </div>
      )}

      <div className={styles.content}>
        {tagline && <div className={styles.tagline}>{tagline}</div>}
        {title && <div className={styles.title}>{title}</div>}
        {description && (
          <TextStyle
            className={styles.description}
            fontSize="inherit"
            textAlign="left"
          >
            <BlockContent value={description} />
          </TextStyle>
        )}
        {buttons && buttons.length > 0 && (
          <div className={styles.buttons}>
            <ButtonsContainer align="left">{buttons}</ButtonsContainer>
          </div>
        )}
      </div>
    </div>
  );
};

export default React.memo(FeaturedContentRow);
